import React, { useState, useEffect } from "react";
import Icon from "@mdi/react";
import { mdiCalendarPlus, mdiCheckCircle, mdiAlertCircleOutline, mdiAirplaneTakeoff } from "@mdi/js";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import DateInputWithPicker from "./DateInputWithPicker";
import TimeSelectDropdown from "./TimeSelectDropdown";
import CustomFormInput from "./forms/CustomFormInput";
import { timeToMinutes, minutesToTime, findOverlaps, planNudge } from "../utils/timeOverlap";
import "./AddSlotsModal.css";

/**
 * AddSlotsModal - Add availability for a single day (add-only), home OR travel
 *
 * Mode-aware: pass `travelPeriod` to add a travel day (purple accent + travel banner
 * + travelPeriodId in the payload); omit it for a home day. Travel slots are the same
 * availability_overrides docs, just tagged with travelPeriodId.
 *
 * Mirrors the weekly-setup form (Start + End + Duration → consecutive slots) plus
 * a -15/+15 nudge on the start time (shifts the end to preserve the span) so
 * quarter-hour starts like 9:15 / 9:30 are one tap.
 *
 * Reached two ways, same modal:
 *  - Grid: click an empty cell → `date` + `prefillStartTime` are passed, date fixed.
 *  - Sidebar button: no `date` → a date picker is shown.
 *
 * Saving is additive (POST /overrides upsert-merges) — it never disturbs the day's
 * existing slots or recurring blocks. Removing slots is handled by EditSlotsModal.
 */
const DURATION_OPTIONS = [45, 60, 90, 120];

const generateSlots = (startTime, endTime, duration) => {
    const slots = [];
    let cur = timeToMinutes(startTime);
    const end = timeToMinutes(endTime);
    while (cur + duration <= end) {
        slots.push({
            startTime: minutesToTime(cur),
            endTime: minutesToTime(cur + duration),
            slotDuration: duration,
        });
        cur += duration;
    }
    return slots;
};

// Normalise a day's existing slot (recurring/override/booked) to a plain range.
const toRange = s => ({
    startTime: s.startTime,
    endTime: s.endTime || minutesToTime(timeToMinutes(s.startTime) + (s.duration || s.slotDuration || 60)),
});

const formatTimeDisplay = t => {
    const [hour, minute] = t.split(":").map(Number);
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${String(minute).padStart(2, "0")} ${period}`;
};

const AddSlotsModal = ({
    isOpen,
    date,
    prefillStartTime,
    daySlotsLookup,
    travelPeriod,
    onConfirm,
    onNudge,
    onCancel,
}) => {
    // Travel-aware: when a travelPeriod is passed the modal adds availability to a
    // travel day (purple accent + travel banner + travelPeriodId in the payload).
    const isTravel = !!travelPeriod;
    const [selectedDate, setSelectedDate] = useState("");
    const [startTime, setStartTime] = useState("09:00");
    const [endTime, setEndTime] = useState("10:00");
    const [duration, setDuration] = useState(60);
    // True once the user picks an end time themselves. Until then the end is
    // derived (start + one slot) and follows the duration pills.
    const [endTouched, setEndTouched] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [confirmNudge, setConfirmNudge] = useState(false); // showing the "make room" confirm panel
    const [isNudging, setIsNudging] = useState(false);

    // A fixed date means the modal was opened from a grid cell (date not editable)
    const dateIsFixed = !!date;

    // Reset the form each time the modal opens
    useEffect(() => {
        if (isOpen) {
            const start = prefillStartTime || "09:00";
            setSelectedDate(date || "");
            setStartTime(start);
            setDuration(60);
            setEndTime(minutesToTime(timeToMinutes(start) + 60)); // default: a single 60-min slot
            setEndTouched(false);
            setIsSaving(false);
            setConfirmNudge(false);
            setIsNudging(false);
        }
    }, [isOpen, date, prefillStartTime]);

    // Any change to the times/date backs out of the make-room confirm (the plan may differ now)
    useEffect(() => {
        setConfirmNudge(false);
    }, [startTime, endTime, duration, selectedDate]);

    // Duration pills. While the end is still derived (the user has not picked one)
    // it tracks the pill as a single slot, so 120 then 60 goes 7:00-9:00 then
    // 7:00-8:00 and any overlap warning from the longer span clears. An end the
    // user chose is kept, and only extended when it is too short for one slot
    // (7:00-8:00 at 90 min would otherwise yield zero slots).
    const handleDurationChange = mins => {
        setDuration(mins);
        const start = timeToMinutes(startTime);
        if (!endTouched || timeToMinutes(endTime) - start < mins) {
            setEndTime(minutesToTime(start + mins));
        }
    };

    const handleEndTimeChange = t => {
        setEndTouched(true);
        setEndTime(t);
    };

    // Move the start, shifting the end to preserve the span (never less than one
    // slot). Shared by the start dropdown and the -15/+15 nudge buttons so a
    // 7:00-8:00 block moved to 7:15 becomes 7:15-8:15 either way.
    const applyStart = startMins => {
        const span = Math.max(duration, timeToMinutes(endTime) - timeToMinutes(startTime));
        const newStart = Math.max(0, Math.min(startMins, 24 * 60 - duration));
        setStartTime(minutesToTime(newStart));
        setEndTime(minutesToTime(newStart + span));
    };
    const handleStartTimeChange = t => applyStart(timeToMinutes(t));
    const nudgeStart = delta => applyStart(timeToMinutes(startTime) + delta);

    const slots = generateSlots(startTime, endTime, duration);

    // Overlap check: any new slot that overlaps an existing slot for the day is a
    // conflict. This is an ADD-only surface — re-adding a slot that already exists
    // (even an exact 9:00–10:00 duplicate of a 9:00–10:00 slot) is not a meaningful
    // "replace", it's a collision the instructor should see. No same-start exemption:
    // half-open intervals already allow legitimate back-to-back slots (9–10 then 10–11).
    const existingSlots =
        daySlotsLookup && selectedDate && Array.isArray(daySlotsLookup[selectedDate])
            ? daySlotsLookup[selectedDate]
            : [];
    const conflicts = (() => {
        const seen = new Map();
        const others = existingSlots.map(toRange);
        slots.forEach(ns => {
            findOverlaps(ns, others).forEach(c => seen.set(`${c.startTime}-${c.endTime}`, c));
        });
        return Array.from(seen.values());
    })();
    const hasConflict = conflicts.length > 0;

    // If there's a conflict, work out whether the following slots can be shifted to fit.
    const plan = hasConflict ? planNudge(slots, existingSlots) : { shifts: [], blockedReason: null };
    const canNudge = !!onNudge && hasConflict && !plan.blockedReason && plan.shifts.length > 0;

    const nudgeBlockedMessage = reason => {
        if (!reason) return null;
        const t = reason.time ? formatTimeDisplay(reason.time) : "";
        switch (reason.type) {
            case "booked":
                return `There's a booking at ${t} in the way. Cancel it in your Bookings dashboard first, then you can make room.`;
            case "lunch":
                return "These times run into your lunch break. Adjust the start time or duration instead.";
            case "frontOverlap":
                return "These times overlap an earlier slot. Adjust the start time.";
            case "endofday":
                return "There isn't enough room later that day to shift the following slots. Adjust the start time or duration.";
            default:
                return null;
        }
    };

    const handleMakeRoom = async () => {
        if (!canNudge) return;
        setIsNudging(true);
        try {
            await onNudge({
                date: selectedDate,
                addSlots: slots,
                shifts: plan.shifts,
                travelPeriodId: travelPeriod?.id || null,
            });
        } finally {
            setIsNudging(false);
        }
    };

    const handleSave = async () => {
        if (!selectedDate) {
            toast.error("Please select a date");
            return;
        }
        if (slots.length === 0) {
            toast.error("Set the end time after the start time.");
            return;
        }
        if (hasConflict) {
            toast.error("These times overlap existing availability. Adjust the start or duration.");
            return;
        }
        setIsSaving(true);
        try {
            await onConfirm({ date: selectedDate, slots, travelPeriodId: travelPeriod?.id || null });
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    const luxonDate = selectedDate ? DateTime.fromISO(selectedDate) : null;

    // Theme accent: travel = the fixed dark travel purple, home = turquoise.
    // NEVER the per-trip travelPeriod.color — that arbitrary colour belongs ONLY in
    // the destination pill, not the title/icons/accents (matches the pre-merge modal).
    const accentColor = isTravel
        ? "var(--dl-color-purple-default)"
        : "var(--dl-color-turquoise-default)";

    return (
        <div className="add-home-slots-overlay">
            <div className={`add-home-slots-modal${isTravel ? " add-home-slots-modal--travel" : ""}`}>
                {/* Header */}
                <div className="add-home-slots-header">
                    <div className="add-home-slots-header-content">
                        <Icon path={isTravel ? mdiAirplaneTakeoff : mdiCalendarPlus} size={1.2} color={accentColor} />
                        <div>
                            <h2 style={{ color: accentColor }}>
                                {isTravel ? "Add Travel Availability" : "Add Home Availability"}
                            </h2>
                            {dateIsFixed && luxonDate && (
                                <p className="date-subtitle">{luxonDate.toFormat("EEEE, MMMM d, yyyy")}</p>
                            )}
                        </div>
                    </div>
                    <button className="add-home-slots-close" onClick={onCancel}>
                        ×
                    </button>
                </div>

                {/* Context banner */}
                {isTravel ? (
                    <div className="travel-context-banner">
                        <div
                            className="travel-destination"
                            style={{
                                borderColor: `${travelPeriod.color}80`,
                                backgroundColor: `${travelPeriod.color}15`,
                            }}>
                            <Icon path={mdiAirplaneTakeoff} size={0.7} style={{ color: travelPeriod.color }} />
                            <span>{travelPeriod.destination}</span>
                        </div>
                        {travelPeriod.timezoneAbbr && (
                            <div className="travel-timezone">
                                {travelPeriod.timezoneAbbr} (UTC{travelPeriod.timezoneOffset})
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="home-context-banner">
                        <div className="context-hint">
                            Adds availability to this date only. Your weekly pattern stays unchanged.
                        </div>
                    </div>
                )}

                {/* Body */}
                <div className="add-home-slots-body">
                    {/* Duration first — the slot times below are derived from it */}
                    <div className="form-group">
                        <label className="form-label">Duration per slot</label>
                        <div className="duration-radio">
                            {DURATION_OPTIONS.map(mins => (
                                <CustomFormInput
                                    key={mins}
                                    type="radio"
                                    layout="pill"
                                    variant={isTravel ? "purple" : "turquoise"}
                                    name="add-home-duration"
                                    value={mins}
                                    label={`${mins} min`}
                                    checked={duration === mins}
                                    onChange={() => handleDurationChange(mins)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Date (when opened without a fixed date) + Start + End on one row */}
                    <div className="time-row">
                        {!dateIsFixed && (
                            <div className="form-group">
                                <label className="form-label">Date</label>
                                <DateInputWithPicker value={selectedDate} onChange={setSelectedDate} />
                            </div>
                        )}
                        <div className="form-group">
                            <label className="form-label">Start time</label>
                            <div className="start-time-row">
                                <TimeSelectDropdown
                                    value={startTime}
                                    onChange={handleStartTimeChange}
                                    accent={isTravel ? "purple" : "turquoise"}
                                    ariaLabel="Start time"
                                />
                                <div className="nudge-buttons">
                                    <button type="button" onClick={() => nudgeStart(-15)}>
                                        -15
                                    </button>
                                    <button type="button" onClick={() => nudgeStart(15)}>
                                        +15
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="form-label">End time</label>
                            <TimeSelectDropdown
                                value={endTime}
                                onChange={handleEndTimeChange}
                                accent={isTravel ? "purple" : "turquoise"}
                                ariaLabel="End time"
                            />
                        </div>
                        <p className="form-hint time-row-hint">
                            Use -15 / +15 for quarter-hour starts (9:15, 9:30). Extend the end time to add
                            several consecutive slots.
                        </p>
                    </div>

                    {/* Overlap warning — sits under the time fields so it's visible without scrolling; blocks the add */}
                    {hasConflict && !confirmNudge && (
                        <div className="add-home-conflict">
                            <Icon path={mdiAlertCircleOutline} size={0.9} color="var(--dl-color-danger)" />
                            <div className="add-home-conflict-body">
                                <strong>Overlaps existing availability</strong>
                                <p>
                                    Clashes with{" "}
                                    {conflicts
                                        .map(
                                            c => `${formatTimeDisplay(c.startTime)} to ${formatTimeDisplay(c.endTime)}`
                                        )
                                        .join(", ")}
                                    .
                                    {plan.blockedReason
                                        ? ` ${nudgeBlockedMessage(plan.blockedReason)}`
                                        : " Adjust the start time or duration, or click make room button to shift the following slots as well."}
                                </p>
                                {canNudge && (
                                    <button
                                        type="button"
                                        className="add-home-makeroom-btn"
                                        onClick={() => setConfirmNudge(true)}>
                                        Make room (shift later slots)
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Make-room confirm — lists the exact moves before applying */}
                    {hasConflict && confirmNudge && canNudge && (
                        <div className="add-home-nudge-confirm">
                            <strong>Make room by shifting these later slots?</strong>
                            <ul className="add-home-nudge-moves">
                                {plan.shifts.map((s, i) => (
                                    <li key={i}>
                                        {formatTimeDisplay(s.oldStartTime)} moves to {formatTimeDisplay(s.newStartTime)}
                                    </li>
                                ))}
                            </ul>
                            <p className="add-home-nudge-note">
                                Your weekly pattern is unchanged. This only affects this date.
                            </p>
                            <div className="add-home-nudge-actions">
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => setConfirmNudge(false)}
                                    disabled={isNudging}>
                                    Back
                                </button>
                                <button
                                    type="button"
                                    className="btn-primary home-context"
                                    onClick={handleMakeRoom}
                                    disabled={isNudging}>
                                    {isNudging ? "Making room..." : "Move and add times"}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Preview */}
                    <div className="add-home-preview">
                        <div className="summary-header">
                            <Icon path={mdiCheckCircle} size={0.8} color={accentColor} />
                            <span>
                                Will add {slots.length} slot{slots.length !== 1 ? "s" : ""}
                            </span>
                        </div>
                        {slots.length > 0 ? (
                            <div className="add-home-preview-list">
                                {slots.map((s, i) => (
                                    <div key={i} className="add-home-preview-item">
                                        {formatTimeDisplay(s.startTime)} - {formatTimeDisplay(s.endTime)}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="add-home-preview-empty">Set the end time after the start time.</p>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="add-home-slots-footer">
                    <button className="btn-secondary" onClick={onCancel} disabled={isSaving || isNudging}>
                        Cancel
                    </button>
                    <button
                        className="btn-primary home-context"
                        onClick={handleSave}
                        disabled={isSaving || slots.length === 0 || !selectedDate || hasConflict}>
                        {isSaving ? "Adding..." : "Add times"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddSlotsModal;
